/**
 * Mobile navigation drawer (below md): hamburger in the header opens the
 * same sections as the desktop sidebar.
 */

import { useState } from 'react';
import { Menu, Settings, X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/brand';
import { Button } from '@/components/ui';

import { NavItemLink } from './NavItemLink';
import { NavSections } from './NavSections';
import { SETTINGS_HREF, useActiveNavHref } from './useActiveNavHref';

export function MobileNav() {
  const { t } = useTranslation('nav');
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const activeHref = useActiveNavHref();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('aria.openMenu')}>
          <Menu className="h-4 w-4" aria-hidden />
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 md:hidden" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r bg-card shadow-soft md:hidden"
        >
          <DialogPrimitive.Title className="sr-only">{t('aria.menu')}</DialogPrimitive.Title>
          <div className="flex h-16 items-center justify-between border-b px-4">
            <BrandLogo className="h-8" />
            <DialogPrimitive.Close className="focus-ring rounded-md p-1" aria-label={t('aria.closeMenu')}>
              <X className="h-4 w-4" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          <nav className="flex-1 overflow-y-auto p-4">
            <NavSections onNavigate={close} />
          </nav>
          <div className="border-t p-4">
            <NavItemLink item={{ labelKey: 'items.settings', href: SETTINGS_HREF, icon: Settings }} active={activeHref === SETTINGS_HREF} onNavigate={close} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
